import { Controller } from "@hotwired/stimulus";

/**
 * GO4S Design System — Tabs.
 * Bascule les panneaux `.ds-tabpanel[data-tab="ID"]` situés dans le même parent
 * que la liste d'onglets. Spec : docs/design-system/REFERENCE.md §3
 */

// Compteur module : préfixe unique par instance, pour que deux tabsets
// partageant des ids d'onglets génériques ("overview", "month"…) sur une même
// page ne produisent pas d'ids DOM dupliqués ni d'ARIA croisé.
let nextUid = 0;

export default class extends Controller {
  static targets = ["tab"];
  static values = { active: String };

  connect() {
    this.uid = this.element.id || `ds-tabs-${(nextUid += 1)}`;
    this.wireAria();
    this.show(this.activeValue || this.tabTargets[0]?.dataset.tab);
  }

  select(event) {
    this.show(event.currentTarget.dataset.tab);
  }

  root() {
    return this.element.closest("[data-ds-tabs-root]") || this.element.parentElement || document;
  }

  // Les panneaux de ce jeu d'onglets : pas ceux d'un jeu imbriqué (des
  // onglets dans un onglet), qui ont leur propre racine [data-ds-tabs-root].
  panels() {
    const root = this.root();
    return Array.from(root.querySelectorAll(".ds-tabpanel[data-tab]")).filter((panel) => {
      const owner = panel.parentElement?.closest("[data-ds-tabs-root]");
      return !owner || owner === root || !root.contains(owner);
    });
  }

  // Câble ids + sémantique tab/tabpanel au runtime, scopés par instance (les
  // panneaux sont rendus par les pages, hors du partial). Idempotent.
  wireAria() {
    this.tabTargets.forEach((t) => {
      const key = t.dataset.tab;
      t.id = `${this.uid}-tab-${key}`;
      const panel = this.panels().find((p) => p.dataset.tab === key);
      if (!panel) return;
      if (panel.getAttribute("role") !== "tabpanel") panel.setAttribute("role", "tabpanel");
      if (!panel.id) panel.id = `${this.uid}-panel-${key}`;
      t.setAttribute("aria-controls", panel.id);
      panel.setAttribute("aria-labelledby", t.id);
      panel.tabIndex = 0;
    });
  }

  show(id) {
    if (!id) return;
    this.activeValue = id;
    this.tabTargets.forEach((t) => {
      const on = t.dataset.tab === id;
      t.setAttribute("aria-selected", String(on));
      // Focus roving : seule la tab active est tabulable, les autres en flèches.
      t.tabIndex = on ? 0 : -1;
    });
    this.panels().forEach((p) => {
      p.hidden = p.dataset.tab !== id;
    });
  }

  // Navigation clavier du tablist (WAI-ARIA APG) : flèches, Home, End.
  onKey(event) {
    const keys = ["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown", "Home", "End"];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const tabs = this.tabTargets;
    const current = tabs.indexOf(event.currentTarget);
    let next = current;
    if (event.key === "Home") next = 0;
    else if (event.key === "End") next = tabs.length - 1;
    else if (event.key === "ArrowRight" || event.key === "ArrowDown") next = (current + 1) % tabs.length;
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = (current - 1 + tabs.length) % tabs.length;
    const target = tabs[next];
    if (target) {
      this.show(target.dataset.tab);
      target.focus();
    }
  }
}
